/**
 * Round 76 — SHARED video workflow next_action constants. Mirror of
 * /app/backend/services/video_workflow.py terminal states.
 *
 * Every video page on PayGBot (Create Content, Weekly Planner,
 * Stories, Run My Week, My Projects) imports this constant set and
 * the `getVideoNextAction(asset)` resolver. No page is allowed to
 * compute video readiness differently.
 */

export const NEXT_ACTION = {
  NEEDS_SCRIPT: 'needs_script',
  NEEDS_VOICE_PREVIEW: 'needs_voice_preview',
  READY_TO_GENERATE_VIDEO: 'ready_to_generate_video',
  GENERATING_VIDEO: 'generating_video',
  VIDEO_FAILED: 'video_failed',
  VIDEO_READY: 'video_ready',
  READY_TO_APPROVE: 'ready_to_approve',
};

const IN_FLIGHT_STATUSES = new Set([
  'queued', 'processing', 'voiceover_processing', 'video_processing',
  'composing', 'validating', 'pending',
]);

const FAILED_STATUSES = new Set([
  'failed', 'failed_no_url', 'blocked_max_retries', 'needs_repair',
]);

const isPublicUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const u = url.trim();
  if (!/^https?:\/\//i.test(u)) return false;
  const lowered = u.toLowerCase();
  if (lowered.includes('localhost') || lowered.includes('127.0.0.1') || lowered.includes('0.0.0.0')) {
    return false;
  }
  return true;
};

const isVideoPost = (asset) => {
  const ct = String(asset.content_type || '').toLowerCase();
  const mt = String(asset.media_type || '').toLowerCase();
  if (['reel', 'video', 'short', 'shorts', 'tiktok'].includes(ct)) return true;
  if (['video', 'reel', 'short'].includes(mt)) return true;
  if (ct || mt) return false;
  return Boolean(asset.video_prompt);
};

/**
 * Compute the next action for a video asset. Mirrors the Python
 * `services.video_workflow.compute_next_action`. If the backend
 * already provided `readiness.next_action`, prefer that (it's the
 * SAME logic but with extra DB context like job status from
 * video_jobs that the frontend may not have).
 *
 * @param {object} asset — any of the 5 surface shapes (planner post,
 *   create-content draft, story asset, generated_content row, queue row)
 * @returns {{ next_action: string, voice_ready: boolean, video_ready: boolean,
 *             is_ready_to_approve: boolean, reasons: string[] }}
 */
export const getVideoNextAction = (asset = {}) => {
  // Server-of-record fast path — backend's compute_next_action is the
  // authority. Trust readiness.next_action when present.
  const backendNextAction = asset?.readiness?.next_action;
  if (backendNextAction && Object.values(NEXT_ACTION).includes(backendNextAction)) {
    return {
      next_action: backendNextAction,
      voice_ready: Boolean(asset.readiness.voice_ready),
      video_ready: Boolean(asset.readiness.video_ready),
      is_ready_to_approve: Boolean(asset.readiness.is_ready_to_approve),
      reasons: asset.readiness.blocked_reasons || [],
    };
  }

  // Local fallback — compute from raw fields. Used by Create Content
  // before any backend round-trip and by tests.
  const script = (asset.script || asset.voiceover_script || '').trim();
  const videoPrompt = (asset.video_prompt || asset.prompt || '').trim();
  const caption = (asset.caption || asset.text || '').trim();
  const platform = String(asset.platform || '').trim().toLowerCase();
  const voicePreview = asset.voice_preview || {};
  const audioUrl = (asset.audio_url || voicePreview.audio_url || '').trim();
  const audioDuration = Number(asset.audio_duration || voicePreview.audio_duration || 0);
  const videoStatus = String(asset.video_status || asset.status || '').toLowerCase();
  const videoUrl = (asset.video_url || asset.final_video_url || '').trim();

  const voiceReady = Boolean(audioUrl && audioDuration > 0);
  const videoReady = videoStatus === 'completed' && isPublicUrl(videoUrl);
  const reasons = [];

  if (!isVideoPost(asset)) {
    if (!caption) {
      reasons.push('Caption is missing.');
      return { next_action: NEXT_ACTION.NEEDS_SCRIPT, voice_ready: false, video_ready: false, is_ready_to_approve: false, reasons };
    }
    return { next_action: NEXT_ACTION.READY_TO_APPROVE, voice_ready: false, video_ready: false, is_ready_to_approve: true, reasons: [] };
  }

  if (!script) {
    reasons.push('Voiceover script is required.');
    return { next_action: NEXT_ACTION.NEEDS_SCRIPT, voice_ready: false, video_ready: false, is_ready_to_approve: false, reasons };
  }

  if (FAILED_STATUSES.has(videoStatus)) {
    reasons.push('Previous video generation failed.');
    return { next_action: NEXT_ACTION.VIDEO_FAILED, voice_ready: voiceReady, video_ready: false, is_ready_to_approve: false, reasons };
  }

  if (IN_FLIGHT_STATUSES.has(videoStatus)) {
    reasons.push('Video is still generating.');
    return { next_action: NEXT_ACTION.GENERATING_VIDEO, voice_ready: voiceReady, video_ready: false, is_ready_to_approve: false, reasons };
  }

  if (videoReady) {
    const approvalBlockers = [];
    if (!caption) approvalBlockers.push('Caption is missing.');
    if (!platform) approvalBlockers.push('Platform is not set.');
    if (!isPublicUrl(videoUrl)) approvalBlockers.push('Final video URL is non-public.');
    if (approvalBlockers.length) {
      return { next_action: NEXT_ACTION.VIDEO_READY, voice_ready: true, video_ready: true, is_ready_to_approve: false, reasons: approvalBlockers };
    }
    return { next_action: NEXT_ACTION.READY_TO_APPROVE, voice_ready: true, video_ready: true, is_ready_to_approve: true, reasons: [] };
  }

  if (!voiceReady) {
    reasons.push('Preview the voiceover first.');
    return { next_action: NEXT_ACTION.NEEDS_VOICE_PREVIEW, voice_ready: false, video_ready: false, is_ready_to_approve: false, reasons };
  }

  if (!videoPrompt) {
    reasons.push('Video prompt is required.');
    return { next_action: NEXT_ACTION.NEEDS_VOICE_PREVIEW, voice_ready: true, video_ready: false, is_ready_to_approve: false, reasons };
  }

  return { next_action: NEXT_ACTION.READY_TO_GENERATE_VIDEO, voice_ready: true, video_ready: false, is_ready_to_approve: false, reasons: [] };
};
